import React from 'react';
import PropTypes from 'prop-types';
import {I18n} from 'react-redux-i18n';
import {isEqual, isEmpty} from 'lodash';
import ViPropTypes from '../../propTypes';
import {getBrakeEvents, getClutchEvents, getCruiseEvents, getOerTime, getVideoTime} from '../../util/media';
import formatOer from '../../util/translateOer';
import {getOerIconName} from '../../util/oer';

const getOerEvent = media => {
  const oerTime = getOerTime(media);

  if (!oerTime) {
    return null;
  }

  if (!oerTime.trigger && !oerTime.second) {
    return null;
  }

  return {
    label: formatOer(oerTime.trigger),
    type: getOerIconName(oerTime.trigger),
    time: formatSecond(oerTime.second)
  };
};

const getBrakeEventList = media => {
  return parseEvents(getBrakeEvents(media), media);
};

const getClutchEventList = media => {
  return parseEvents(getClutchEvents(media), media);
};

const getCruiseEventList = media => {
  return parseEvents(getCruiseEvents(media), media);
};

const parseEvents = (events, media) => {
  const video = getVideoTime(media);
  return events.map(event => {
    let endSecond = event.endSecond + 1;

    if (endSecond > video.duration) {
      endSecond = video.duration;
    }

    return {
      ...event,
      timeRange: `${formatSecond(event.startSecond)} - ${formatSecond(endSecond)}`
    };
  });
};

const formatSecond = seconds => {
  const minutes = Math.floor(seconds / 60);
  const missingSeconds = seconds % 60;

  const padedMinutes = String(minutes).padStart(2, '0');
  const padedSeconds = String(missingSeconds).padStart(2, '0');

  return `${padedMinutes}:${padedSeconds}`;
};

const EventChip = ({type, label, time}) => {
  return (
    <div className={`oerchip`}>
      <span className={`oericon oericon__${type}`}></span>
      {label}&nbsp;{time}
    </div>
  );
};

export default class OerEventsDashboard extends React.Component {

  static propTypes = {
    showEventLabel: PropTypes.bool,
    media: ViPropTypes.Media
  }

  static defaultProps = {
    showEventLabel: false
  }

  state = {
    media: null,
    oerEvent: {},
    brakeEvents: [],
    clutchEvents: [],
    cruiseEvents: []
  }

  hasOerEvent() {
    return !isEmpty(this.state.oerEvent);
  }

  hasTimelineEvents() {
    const {brakeEvents, clutchEvents, cruiseEvents} = this.state;

    if (!isEmpty(brakeEvents)) {
      return true;
    }

    if (!isEmpty(clutchEvents)) {
      return true;
    }

    if (!isEmpty(cruiseEvents)) {
      return true;
    }

    return false;
  }

  renderCustomEvents() {
    const {oerEvent} = this.state;

    if (!oerEvent) {
      return null;
    }

    return (
      <EventChip
        type={oerEvent.type}
        label={oerEvent.label}
        time={oerEvent.time}
      />
    );
  }

  renderBrakeEvents() {
    return this.renderEvents(this.state.brakeEvents, 'brake');
  }

  renderClutchEvents() {
    return this.renderEvents(this.state.clutchEvents, 'clutch');
  }

  renderCruiseEvents() {
    return this.renderEvents(this.state.cruiseEvents, 'cruise');
  }

  renderEvents(events, type) {
    let label = '';

    if (this.props.showEventLabel) {
      label = I18n.t(`page.playerPage.dashboard.${type}`);
    }

    return (
      <React.Fragment>
        {events.map(event => (
          <EventChip
            key={`${type}_${event.time}`}
            type={type}
            label={label}
            time={event.timeRange}
          />
        ))}
      </React.Fragment>
    );
  }

  renderEventsWihoutLabel() {
    return (
      <div className="dashboard__oerevents">
        {this.renderCustomEvents()}
        {this.renderBrakeEvents()}
        {this.renderClutchEvents()}
        {this.renderCruiseEvents()}
      </div>
    );
  }

  renderEventsWithLabel() {
    return (
      <>
        {this.hasOerEvent() && (
          <div className="dashboard__oerpanel">
            <span className="dashboard__eventlabel">
              {I18n.t(`page.playerPage.dashboard.eventType`)}
            </span>

            <div className="dashboard__oerevents">
              {this.renderCustomEvents()}
            </div>
          </div>
        )}

        {this.hasTimelineEvents() && (
          <div className="dashboard__oerpanel">
            <span className="dashboard__eventlabel">
              Timeline Events
            </span>

            <div className="dashboard__oerevents">
              {this.renderBrakeEvents()}
              {this.renderClutchEvents()}
              {this.renderCruiseEvents()}
            </div>
          </div>
        )}
      </>
    );
  }

  render() {
    if (this.props.showEventLabel) {
      return this.renderEventsWithLabel();
    }

    return this.renderEventsWihoutLabel();
  }

  static getDerivedStateFromProps(props, state) {
    const currentMedia = state.media;
    const newMedia = props.media;

    if (isEqual(currentMedia, newMedia)) {
      return null;
    }

    const oerEvent = getOerEvent(props.media);
    const brakeEvents = getBrakeEventList(props.media);
    const clutchEvents = getClutchEventList(props.media);
    const cruiseEvents = getCruiseEventList(props.media);

    return {
      media: newMedia,
      oerEvent,
      brakeEvents,
      clutchEvents,
      cruiseEvents
    };
  }

}
